/**
 * Organizations — admin + user surfaces.
 *
 * Admin routes (`/admin/organizations/...`) are internal-only. The PATCH
 * edit goes through the user route because the backend doesn't expose
 * `PATCH /admin/organizations/{id}`; platform admins bypass membership.
 */
import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from '../client.js';
import type {
  AdminOrganization,
  Organization,
  ReactivateInitiate,
  UpdateOrganizationBody,
} from '../types.js';

export interface ListOrganizationsFilters {
  status?: string;
}

export function listOrganizations(filters: ListOrganizationsFilters = {}): Promise<AdminOrganization[]> {
  return apiGet<AdminOrganization[]>('/api/v1/admin/organizations', filters.status ? { status: filters.status } : undefined);
}

export function getAdminOrganization(id: string): Promise<AdminOrganization> {
  return apiGet<AdminOrganization>(`/api/v1/admin/organizations/${id}`);
}

export function setOrgTaskConcurrency(
  id: string,
  maxConcurrentTasks: number | null,
): Promise<AdminOrganization> {
  return apiPut<AdminOrganization>(`/api/v1/admin/organizations/${id}/task-concurrency`, {
    max_concurrent_tasks: maxConcurrentTasks,
  });
}

export function deactivateOrganization(id: string): Promise<AdminOrganization> {
  return apiPost<AdminOrganization>(`/api/v1/admin/organizations/${id}/deactivate`);
}

export function reactivateInitiate(id: string): Promise<ReactivateInitiate> {
  return apiPost<ReactivateInitiate>(`/api/v1/admin/organizations/${id}/reactivate-initiate`);
}

/** Hard-delete an inactive organization. Backend returns 409 if still active. */
export function deleteOrganization(id: string): Promise<void> {
  return apiDelete<void>(`/api/v1/admin/organizations/${id}`);
}

/** User route — works for platform-admins + org members. */
export function getOrganization(id: string): Promise<Organization> {
  return apiGet<Organization>(`/api/v1/organizations/${id}`);
}

/** User route — platform-admin bypasses membership, client-admin required otherwise. */
export function updateOrganization(id: string, body: UpdateOrganizationBody): Promise<Organization> {
  return apiPatch<Organization>(`/api/v1/organizations/${id}`, body);
}
